import request from 'superagent';
import noCache from 'superagent-no-cache';

import { getCsrfToken } from '../../utils';
import { flattenNestedObject } from './flattenNestedObject';
import { parseResponseJson } from './utils';

const Loader = {
    /**
     * Loads JSON and returns the request so callers can cancel it.
     * Callbacks return Redux actions. A separate error callback is optional.
     */
    getJson: function (url, queryParams, dispatch, callback, errorCallback) {
        const dispatchCallback =
            typeof callback === 'function'
                ? (payload) => dispatch(callback(payload))
                : null;
        const dispatchError =
            typeof errorCallback === 'function'
                ? (error) => dispatch(errorCallback(error))
                : null;

        return request
            .get(url)
            .use(noCache)
            .set('Accept', 'application/json')
            .query(queryParams)
            .end((error, res) => {
                if (error) {
                    console.error(
                        'Loading JSON from ' + url + ' failed: ' + error
                    );
                    console.error('Original error: ' + error.original);
                    // Existing callers receive errors through their original callback.
                    (dispatchError || dispatchCallback)?.(error);
                } else if (res) {
                    // Check HTTP status code - 4xx and 5xx are errors
                    if (res.status >= 400) {
                        console.error(
                            `Loading JSON from ${url} failed with status ${res.status}`
                        );
                        (dispatchError || dispatchCallback)?.(res);
                    } else if (res.error) {
                        console.error(
                            `Loading JSON from ${url} failed: ${res.error}`
                        );
                        dispatchError?.(res);
                    } else {
                        if (!dispatchCallback && !dispatchError) return;

                        let json;
                        try {
                            json = JSON.parse(res.text);
                        } catch (parseError) {
                            if (!dispatchError) throw parseError;
                            dispatchError(parseError);
                            return;
                        }
                        dispatchCallback?.(json);
                    }
                }
            });
    },

    /**
     * Deletes a resource, accepting successful JSON or empty responses.
     *
     * @param {string} url - Resource URL.
     * @param {Function} dispatch - Redux dispatch function.
     * @param {Function|null} callback - Creates the response action, if provided.
     * @param {Function} [cb] - Called after success with parsed JSON or an empty object.
     */
    delete: function (url, dispatch, callback, cb) {
        const dispatchCallback =
            typeof callback === 'function'
                ? (payload) => dispatch(callback(payload))
                : null;

        request
            .delete(url)
            .set('Accept', 'application/json')
            .set('X-CSRF-Token', getCsrfToken())
            .end((error, res) => {
                if (error) {
                    console.error(
                        'Loading JSON from ' + url + ' failed: ' + error
                    );
                    console.error('Original error: ' + error.original);
                    dispatchCallback?.(error);
                } else if (res) {
                    // Check HTTP status code - 4xx and 5xx are errors
                    if (res.status >= 400) {
                        console.error(
                            `Deleting from ${url} failed with status ${res.status}`
                        );
                        dispatchCallback?.(res);
                    } else if (res.error) {
                        console.error(
                            `Deleting from ${url} failed: ${res.error}`
                        );
                    } else {
                        const json = parseResponseJson(res, url);
                        dispatchCallback?.(json);
                        if (typeof cb === 'function') {
                            cb(json);
                        }
                    }
                }
            });
    },

    put: function (
        url,
        params,
        dispatch,
        successCallback,
        errorCallback,
        callback
    ) {
        let req = request.put(url);
        Loader.submit(
            req,
            url,
            params,
            dispatch,
            successCallback,
            errorCallback,
            callback
        );
    },

    post: function (
        url,
        params,
        dispatch,
        successCallback,
        errorCallback,
        callback
    ) {
        let req = request.post(url);
        Loader.submit(
            req,
            url,
            params,
            dispatch,
            successCallback,
            errorCallback,
            callback
        );
    },

    submit: function (
        req,
        url,
        params,
        dispatch,
        successCallback,
        errorCallback,
        cb
    ) {
        let scope = Object.keys(params)[0];
        Object.keys(params[scope]).map((param) => {
            if (
                params[scope][param] !== undefined &&
                params[scope][param] !== null
            ) {
                if (params[scope][param] instanceof File) {
                    // like this it is possible to upload one file through a file-input called data.
                    // you need more file-inputs? change the implementation here!
                    req.attach(`${scope}[${param}]`, params[scope][param]);
                } else {
                    if (Array.isArray(params[scope][param])) {
                        // value is an array
                        params[scope][param].map((elem, elemIndex) => {
                            if (typeof elem === 'object') {
                                // array elements are hashes/ objects
                                Object.keys(elem).map((e) => {
                                    //
                                    // second layer nested e.g. registry_entry[registry_names[registry_name_translations]]
                                    //
                                    // Parameters: {"registry_entry"=>{"workflow_state"=>"public",
                                    // "registry_names_attributes"=>[{"registry_entry_id"=>"28205",
                                    // "translations_attributes"=>[{"locale"=>"de", "id"=>"", "descriptor"=>"sdfsdfsdf"}],
                                    // "name_position"=>"3", "registry_name_type_id"=>"4"}]}, "locale"=>"de", "id"=>"28205"}
                                    //
                                    if (Array.isArray(elem[e])) {
                                        elem[e].map((i, index) => {
                                            Object.keys(i).map((j) => {
                                                if (i[j] && i[j] !== '')
                                                    req.field(
                                                        `${scope}[${param}][${elemIndex}][${e}][${index}][${j}]`,
                                                        i[j]
                                                    );
                                            });
                                        });
                                    } else {
                                        req.field(
                                            `${scope}[${param}][${elemIndex}][${e}]`,
                                            elem[e] || ''
                                        );
                                    }
                                });
                            } else {
                                // array values are some non-complex values like strings or ints
                                req.field(`${scope}[${param}][]`, elem);
                            }
                        });
                    } else if (typeof params[scope][param] === 'object') {
                        // value is a hash/object - flatten it using helper function
                        flattenNestedObject(
                            req,
                            scope,
                            param,
                            params[scope][param]
                        );
                    } else {
                        // normal value
                        req.field(`${scope}[${param}]`, params[scope][param]);
                    }
                }
            } else {
                // clean params from undefined values
                delete params[scope][param];
            }
        });
        req.set('Accept', 'application/json');
        req.set('X-CSRF-Token', getCsrfToken());
        req.end((error, res) => {
            if (error) {
                console.error('Loading JSON from ' + url + ' failed: ' + error);
                console.error('Original error: ' + error.original);
                console.error('URL: ' + url);
                if (typeof errorCallback === 'function') {
                    dispatch(errorCallback(error));
                }
            } else if (res) {
                const json = parseResponseJson(res, url);
                // Check HTTP status code - 4xx and 5xx are errors
                if (res.status >= 400) {
                    console.error(
                        'Loading JSON from ' +
                            url +
                            ' failed with status ' +
                            res.status
                    );
                    if (typeof errorCallback === 'function') {
                        dispatch(errorCallback(json || res));
                    }
                } else if (res.error) {
                    if (typeof errorCallback === 'function') {
                        dispatch(errorCallback(json));
                    } else {
                        console.error(
                            'Loading JSON from ' + url + ' failed: ' + error
                        );
                    }
                } else if (json.error) {
                    if (typeof errorCallback === 'function') {
                        dispatch(errorCallback(json));
                    }
                } else {
                    if (typeof successCallback === 'function') {
                        dispatch(successCallback(json));
                    }
                    if (typeof cb === 'function') {
                        cb(json);
                    }
                }
            }
        });
    },
};

export default Loader;
